import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PenSquare, Plus, RefreshCw, Trash2, Globe, Table as TableIcon, Save, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { asArray } from "@/lib/arrays";

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlog, setEditingBlog] = useState(null);
  const [savingBlog, setSavingBlog] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", is_published: false,
    seo_title: "", seo_description: "", canonical_url: "", og_title: "", og_description: "", 
    og_image: "", twitter_image: "", robots_index: true, robots_follow: true
  });

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image', 'clean']
    ]
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/blogs?published_only=false`, { withCredentials: true });
      setBlogs(asArray(res.data, "blogs"));
    } catch (err) {
      toast.error("Failed to fetch blogs");
    } finally {
      setLoading(false);
    }
  };

  const insertTable = () => {
    const sampleTable = `
      <table style="width:100%; border-collapse: collapse; margin: 18px 0; border: 1px solid rgba(255,255,255,0.15); border-radius: 8px;">
        <thead>
          <tr style="background-color: rgba(0, 255, 136, 0.15);">
            <th style="border: 1px solid rgba(255,255,255,0.2); padding: 12px 14px; text-align: left; font-weight: bold; color: #00FF88;">Concept / Metric</th>
            <th style="border: 1px solid rgba(255,255,255,0.2); padding: 12px 14px; text-align: left; font-weight: bold; color: #00FF88;">Strategy Description</th>
            <th style="border: 1px solid rgba(255,255,255,0.2); padding: 12px 14px; text-align: left; font-weight: bold; color: #00FF88;">Target / Outcome</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Demand Zone Setup</td>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Rally-Base-Rally at Key Level</td>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Minimum 1:2 R:R</td>
          </tr>
          <tr>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Option Credit Spread</td>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Selling OTM Call/Put with Hedge</td>
            <td style="border: 1px solid rgba(255,255,255,0.1); padding: 10px 14px; color: #CBD5E1;">Theta Decay Harvest</td>
          </tr>
        </tbody>
      </table>
      <p><br/></p>
    `;
    setBlogForm(prev => ({
      ...prev,
      content: (prev.content || "") + sampleTable
    }));
    toast.success("Styled table inserted into editor!");
  };

  const saveBlog = async (publishState = true) => {
    if (!blogForm.title || !blogForm.excerpt || !blogForm.content) {
      toast.error("Please fill title, excerpt and content");
      return;
    }
    setSavingBlog(true);
    try {
      const payload = {
        ...blogForm,
        is_published: publishState,
        tags: blogForm.tags ? blogForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
        seo: {
          seo_title: blogForm.seo_title,
          seo_description: blogForm.seo_description,
          canonical_url: blogForm.canonical_url,
          og_title: blogForm.og_title,
          og_description: blogForm.og_description,
          og_image: blogForm.og_image,
          twitter_image: blogForm.twitter_image,
          robots_index: blogForm.robots_index,
          robots_follow: blogForm.robots_follow
        }
      };
      if (editingBlog) {
        await axios.put(`${API}/blogs/${editingBlog}`, payload, { withCredentials: true });
        toast.success(publishState ? "Blog updated & published!" : "Blog updated & saved as Draft!");
      } else {
        await axios.post(`${API}/blogs`, payload, { withCredentials: true });
        toast.success(publishState ? "Blog published successfully!" : "Blog created & saved as Draft!");
      }
      setBlogForm({ 
        title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", is_published: false,
        seo_title: "", seo_description: "", canonical_url: "", og_title: "", og_description: "", 
        og_image: "", twitter_image: "", robots_index: true, robots_follow: true
      });
      setEditingBlog(null);
      fetchBlogs();
    } catch (error) {
      toast.error("Failed to save blog");
    } finally {
      setSavingBlog(false);
    }
  };

  const togglePublish = async (blog) => {
    try {
      const nextState = !blog.is_published;
      await axios.put(`${API}/blogs/${blog.blog_id}`, {
        ...blog,
        is_published: nextState
      }, { withCredentials: true });
      toast.success(nextState ? "Blog published!" : "Blog moved to Drafts!");
      fetchBlogs();
    } catch (error) {
      toast.error("Failed to toggle publish status");
    }
  };

  const deleteBlog = async (blogId) => {
    try {
      if (!window.confirm("Are you sure you want to delete this blog?")) return;
      await axios.delete(`${API}/blogs/${blogId}`, { withCredentials: true });
      toast.success("Blog deleted");
      fetchBlogs();
    } catch (error) {
      toast.error("Failed to delete blog");
    }
  };

  const startEditBlog = (blog) => {
    setBlogForm({
      title: blog.title,
      slug: blog.slug || "",
      excerpt: blog.excerpt,
      content: blog.content || "",
      cover_image: blog.cover_image || "",
      tags: (blog.tags || []).join(", "),
      is_published: blog.is_published,
      seo_title: blog.seo?.seo_title || "",
      seo_description: blog.seo?.seo_description || "",
      canonical_url: blog.seo?.canonical_url || "",
      og_title: blog.seo?.og_title || "",
      og_description: blog.seo?.og_description || "",
      og_image: blog.seo?.og_image || "",
      twitter_image: blog.seo?.twitter_image || "",
      robots_index: blog.seo?.robots_index ?? true,
      robots_follow: blog.seo?.robots_follow ?? true
    });
    setEditingBlog(blog.blog_id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <div className="py-12 text-center"><div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      {/* Blog Form */}
      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]" data-testid="blog-form">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white font-outfit flex items-center gap-2">
            <PenSquare className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
            {editingBlog ? "Edit Blog Post" : "Create Blog Post"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-[#A1A1AA]">Title</Label>
              <Input value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} placeholder="Blog title" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-[#A1A1AA]">Custom Slug (optional)</Label>
              <Input value={blogForm.slug} onChange={e => setBlogForm({...blogForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})} placeholder="my-custom-blog-slug" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white" />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-[#A1A1AA]">Cover Image URL</Label>
              <Input value={blogForm.cover_image} onChange={e => setBlogForm({...blogForm, cover_image: e.target.value})} placeholder="https://..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500 dark:text-[#A1A1AA]">Tags (comma-separated)</Label>
              <Input value={blogForm.tags} onChange={e => setBlogForm({...blogForm, tags: e.target.value})} placeholder="trading, beginner, options" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-500 dark:text-[#A1A1AA]">Excerpt (short description)</Label>
            <Input value={blogForm.excerpt} onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})} placeholder="Brief description..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-gray-500 dark:text-[#A1A1AA]">Content</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={insertTable}
                className="h-8 text-xs font-bold rounded-lg border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1.5"
                title="Insert a pre-styled 3x3 table with headers"
              >
                <TableIcon className="w-3.5 h-3.5" />
                + Insert Styled Table
              </Button>
            </div>
            <div className="bg-white text-black mt-1 rounded-xl overflow-hidden border border-gray-200 dark:border-white/10 quill-blog-editor shadow-xs">
              <ReactQuill 
                theme="snow" 
                value={blogForm.content} 
                onChange={val => setBlogForm({...blogForm, content: val})} 
                modules={quillModules}
                placeholder="Write your article content here. Use the heading dropdown above for H1, H2, H3, H4 and click '+ Insert Styled Table' for comparisons..."
                className="min-h-[340px]"
              />
            </div>
          </div>

          <div className="border border-gray-200 dark:border-white/10 p-4 rounded-xl bg-black/40 space-y-4">
            <h3 className="text-gray-900 dark:text-white font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-emerald-600 dark:text-emerald-400"/> Advanced SEO Settings</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">SEO Meta Title</Label>
                <Input value={blogForm.seo_title} onChange={e => setBlogForm({...blogForm, seo_title: e.target.value})} placeholder="Default will be Page Title if empty" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">Canonical URL</Label>
                <Input value={blogForm.canonical_url} onChange={e => setBlogForm({...blogForm, canonical_url: e.target.value})} placeholder="https://jamadhan.com/..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
            </div>

            <div>
              <Label className="text-gray-500 dark:text-[#A1A1AA]">SEO Meta Description</Label>
              <Input value={blogForm.seo_description} onChange={e => setBlogForm({...blogForm, seo_description: e.target.value})} placeholder="Short description for search engines..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">Open Graph Title</Label>
                <Input value={blogForm.og_title} onChange={e => setBlogForm({...blogForm, og_title: e.target.value})} placeholder="Social share title" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">Open Graph Image URL</Label>
                <Input value={blogForm.og_image} onChange={e => setBlogForm({...blogForm, og_image: e.target.value})} placeholder="https://..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">Open Graph Description</Label>
                <Input value={blogForm.og_description} onChange={e => setBlogForm({...blogForm, og_description: e.target.value})} placeholder="Social share description" className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
              <div>
                <Label className="text-gray-500 dark:text-[#A1A1AA]">Twitter Card Image URL</Label>
                <Input value={blogForm.twitter_image} onChange={e => setBlogForm({...blogForm, twitter_image: e.target.value})} placeholder="https://..." className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1" />
              </div>
            </div>

            <div className="flex gap-6 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={blogForm.robots_index} onChange={e => setBlogForm({...blogForm, robots_index: e.target.checked})} className="rounded border-gray-200 dark:border-white/[0.06]" />
                <span className="text-sm text-gray-500 dark:text-[#A1A1AA]">Index (Allow Search Engines)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={blogForm.robots_follow} onChange={e => setBlogForm({...blogForm, robots_follow: e.target.checked})} className="rounded border-gray-200 dark:border-white/[0.06]" />
                <span className="text-sm text-gray-500 dark:text-[#A1A1AA]">Follow Links</span>
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-200 dark:border-white/10">
            <div className="flex items-center gap-2">
              <Badge className={blogForm.is_published ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-bold" : "bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-xs font-bold"}>
                Status: {blogForm.is_published ? "Will be Published" : "Draft"}
              </Badge>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {editingBlog && (
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={() => { 
                    setEditingBlog(null); 
                    setBlogForm({ 
                      title: "", slug: "", excerpt: "", content: "", cover_image: "", tags: "", is_published: false,
                      seo_title: "", seo_description: "", canonical_url: "", og_title: "", og_description: "", 
                      og_image: "", twitter_image: "", robots_index: true, robots_follow: true
                    }); 
                  }} 
                  className="border-gray-300 dark:border-white/15 text-gray-700 dark:text-zinc-300 rounded-xl h-10 px-4 font-semibold"
                >
                  Cancel
                </Button>
              )}
              <Button 
                type="button"
                disabled={savingBlog}
                onClick={() => saveBlog(false)} 
                variant="outline" 
                className="border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15 font-bold rounded-xl h-10 px-4 shadow-xs"
              >
                <Save className="w-4 h-4 mr-1.5 text-amber-500" />
                {editingBlog ? "Update as Draft" : "Save Draft"}
              </Button>
              <Button 
                type="button"
                disabled={savingBlog}
                onClick={() => saveBlog(true)} 
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-black shadow-md rounded-xl h-10 px-5"
                data-testid="save-blog-btn"
              >
                <Globe className="w-4 h-4 mr-1.5" />
                {editingBlog ? "Update & Publish" : "Publish Blog"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Blog List */}
      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06]" data-testid="blog-list">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-gray-900 dark:text-white font-outfit">All Blog Posts ({blogs.length})</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchBlogs} className="text-gray-500 dark:text-[#A1A1AA] hover:text-gray-900 dark:text-white">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {blogs.length === 0 ? (
            <p className="text-gray-400 dark:text-[#52525B] text-center py-8">No blog posts yet. Create your first one above!</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 dark:border-white/[0.06] hover:bg-transparent">
                  <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Title & URL</TableHead>
                  <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Status</TableHead>
                  <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Views</TableHead>
                  <TableHead className="text-gray-500 dark:text-[#A1A1AA]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blogs.map(b => (
                  <TableRow key={b.blog_id} className="border-gray-200 dark:border-white/[0.06] hover:bg-gray-50 dark:bg-white/[0.03]" data-testid={`blog-row-${b.blog_id}`}>
                    <TableCell>
                      <div>
                        <p className="text-gray-900 dark:text-white text-sm font-semibold">{b.title}</p>
                        <a 
                          href={`/blog/${b.slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline inline-flex items-center gap-1 mt-0.5"
                        >
                          /blog/{b.slug} <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => togglePublish(b)}
                        title="Click to toggle Draft / Published"
                        className="cursor-pointer transition-transform hover:scale-105"
                      >
                        <Badge className={b.is_published ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 border text-xs font-bold rounded-full px-2.5 py-0.5" : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 border text-xs font-bold rounded-full px-2.5 py-0.5"}>
                          {b.is_published ? "Published" : "Draft"}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell className="text-gray-500 dark:text-[#A1A1AA]">{b.views || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => startEditBlog(b)} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 h-8 px-2" data-testid={`edit-blog-${b.blog_id}`}>
                          <PenSquare className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteBlog(b.blog_id)} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 h-8 px-2" data-testid={`delete-blog-${b.blog_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <style>{`
        .quill-blog-editor .ql-toolbar.ql-snow {
          background: #f8fafc;
          border-color: #cbd5e1;
          border-top-left-radius: 0.75rem;
          border-top-right-radius: 0.75rem;
          padding: 10px;
        }
        .dark .quill-blog-editor .ql-toolbar.ql-snow {
          background: #0B0F17;
          border-color: rgba(255, 255, 255, 0.12);
        }
        .dark .quill-blog-editor .ql-stroke {
          stroke: #cbd5e1 !important;
        }
        .dark .quill-blog-editor .ql-fill {
          fill: #cbd5e1 !important;
        }
        .dark .quill-blog-editor .ql-picker {
          color: #cbd5e1 !important;
          font-weight: 600;
        }
        .dark .quill-blog-editor .ql-picker-options {
          background-color: #0B0F17 !important;
          border-color: rgba(255, 255, 255, 0.15) !important;
        }
        .dark .quill-blog-editor .ql-picker-item {
          color: #cbd5e1 !important;
        }
        .dark .quill-blog-editor .ql-picker-item:hover {
          color: #00FF88 !important;
        }
        .quill-blog-editor .ql-container.ql-snow {
          border-color: #cbd5e1;
          border-bottom-left-radius: 0.75rem;
          border-bottom-right-radius: 0.75rem;
          font-family: inherit;
          font-size: 15px;
        }
        .dark .quill-blog-editor .ql-container.ql-snow {
          border-color: rgba(255, 255, 255, 0.12);
          background: #05070B;
          color: #f1f5f9;
        }
        .quill-blog-editor .ql-editor {
          min-height: 320px;
          line-height: 1.75;
        }
      `}</style>
    </div>
  );
};

export default BlogManager;
